import React from 'react';
import { Helmet } from 'react-helmet-async';

interface Scene7Config {
  widths?: number[];
  imageParams?: string;
  imagePreset?: string;
  mediaSizes?: string;
}

interface Scene7ImageProps {
  imageName: string;
  altText: string;
  width?: string;
  height?: string;
  preload?: boolean;
  lazyLoad?: boolean;
  config?: Partial<Scene7Config>;
}

/**
 * Default configuration
 */
const scene7Source = '//waitrose-prod.scene7.com/is/image/waitroseprod/';
const defaultConfig = {
  imagePreset: '$Waitrose-Image-Preset-95$',
  imageParams: '',
  widths: [480, 768, 1024],
  mediaSizes: '100vw',
};

/**
 * Scene7Image component
 *
 * This component can be used for imagery that just needs different image sizes for different devices
 * If it is an art direction image you need, please use the Scene7Picture component instead
 *
 * Renders an image component using the name of the image from AEM. Both the 'imageName' and 'altText' prop are required.
 * If there is no 'config' object passed to the component, it will use the default values above. If you wish to override these
 * settings, it can be achieved by creating a config object and passing this to the component,
 * ensuring that the data types are the same as what is set here: 'Scene7Config'.
 *
 * The current behaviour is to render an img tag that renders 3 source sets, one for mobile, tablet and desktop.
 * Currently the default fallback image is using the mobile image.
 *
 * Documentation for the image API using imageParams:
 * https://experienceleague.adobe.com/docs/dynamic-media-developer-resources/image-serving-api/image-serving-api/http-protocol-reference/command-reference/c-command-reference.html
 */
const Scene7Image = ({
  imageName,
  altText,
  width,
  height,
  preload = false,
  lazyLoad = true,
  config,
}: Scene7ImageProps) => {
  const {
    widths = defaultConfig.widths,
    imagePreset = defaultConfig.imagePreset,
    imageParams = defaultConfig.imageParams,
    mediaSizes = defaultConfig.mediaSizes,
  } = config || {};

  /**
   * Generate the srcset string based on the provided or default options.
   *
   * @returns {String} The generated srcset string
   */
  const generateSrcSet = () => {
    return widths
      .map((imageSize: number) => {
        return `${scene7Source}${imageName}?${imagePreset}${imageParams}&wid=${imageSize} ${imageSize}w`;
      })
      .join(', ');
  };

  return (
    <>
      {preload && (
        <Helmet>
          <link
            rel="preload"
            as="image"
            href={`${scene7Source}${imageName}?${imagePreset}${imageParams}&wid=${widths[0]}`}
            imageSrcSet={generateSrcSet()}
            imageSizes={mediaSizes}
            data-testid="link-preload"
          />
        </Helmet>
      )}
      <img
        alt={altText}
        {...(lazyLoad && { loading: 'lazy' })}
        {...(width && { width })}
        {...(height && { height })}
        src={`${scene7Source}${imageName}?${imagePreset}${imageParams}&wid=${widths[0]}`}
        srcSet={generateSrcSet()}
        sizes={mediaSizes}
      />
    </>
  );
};

export default Scene7Image;
